// src/components/cost-simulator/CostInsightsTabs.tsx
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3, Cloud, Server, BookOpen, Calculator } from 'lucide-react';
import { CloudCostComparePanel } from './CloudCostComparePanel';
import { OnPremTcoPanel } from './OnPremTcoPanel';
import type { NewSizingSnapshot } from '@/sizing/newSizingEngine';
import type {
  CostSimulatorConfig,
  MonthlyRollupEnhanced,
  CostComparisonResult,
} from '@/lib/costCalculations';
import { formatCurrency, formatTokens } from '@/lib/costCalculations';

interface CostInsightsTabsProps {
  snapshot: NewSizingSnapshot | null;
  config: CostSimulatorConfig;
  monthlyRollups: MonthlyRollupEnhanced[];
  comparison: CostComparisonResult;
  monthlyTokens: { inputTokens: number; outputTokens: number; totalTokens: number };
  onPremBreakdown: {
    capexTotal: number;
    monthlyCapexAmortized: number;
    monthlyPowerCost: number;
    monthlyAdminCost: number;
    monthlyFacilitiesCost: number;
    monthlyPersonnelCost: number;
    monthlyOpEx: number;
    monthlyTco: number;
  };
  serverCount: number;
}

export const CostInsightsTabs = ({
  snapshot,
  config,
  monthlyRollups,
  comparison,
  monthlyTokens,
  onPremBreakdown,
  serverCount,
}: CostInsightsTabsProps) => {
  return (
    <Tabs defaultValue="summary" className="w-full">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="summary" className="text-xs sm:text-sm">
          <BarChart3 className="h-4 w-4 mr-1 hidden sm:inline" />
          Summary
        </TabsTrigger>
        <TabsTrigger value="cloud" className="text-xs sm:text-sm">
          <Cloud className="h-4 w-4 mr-1 hidden sm:inline" />
          Cloud Cost
        </TabsTrigger>
        <TabsTrigger value="onprem" className="text-xs sm:text-sm">
          <Server className="h-4 w-4 mr-1 hidden sm:inline" />
          On-Prem TCO
        </TabsTrigger>
        <TabsTrigger value="methodology" className="text-xs sm:text-sm">
          <BookOpen className="h-4 w-4 mr-1 hidden sm:inline" />
          Methodology
        </TabsTrigger>
      </TabsList>

      {/* Summary Tab */}
      <TabsContent value="summary" className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              Cost Analysis Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <div className="text-xs text-muted-foreground mb-1">Monthly Cloud</div>
                <div className="text-lg font-bold">
                  {formatCurrency(monthlyRollups[0]?.monthlyCloudCost ?? 0)}
                </div>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <div className="text-xs text-muted-foreground mb-1">Monthly On-Prem</div>
                <div className="text-lg font-bold">
                  {formatCurrency(onPremBreakdown.monthlyOpEx)}
                </div>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <div className="text-xs text-muted-foreground mb-1">CapEx (Upfront)</div>
                <div className="text-lg font-bold">
                  {formatCurrency(onPremBreakdown.capexTotal, true)}
                </div>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <div className="text-xs text-muted-foreground mb-1">Monthly Tokens</div>
                <div className="text-lg font-bold">
                  {formatTokens(monthlyTokens.totalTokens)}
                </div>
              </div>
            </div>

            {/* Token Throughput from Engine */}
            <div className="bg-primary/5 rounded-lg p-4">
              <h4 className="text-sm font-medium mb-3">Token Throughput (from Sizing Engine)</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Avg Prompt TPS:</span>
                  <span className="ml-2 font-mono">{(snapshot?.avgPromptTokensPerSec ?? 0).toFixed(1)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Avg Decode TPS:</span>
                  <span className="ml-2 font-mono">{(snapshot?.avgDecodeTokensPerSec ?? 0).toFixed(1)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Monthly Input:</span>
                  <span className="ml-2 font-mono">{formatTokens(monthlyTokens.inputTokens)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Monthly Output:</span>
                  <span className="ml-2 font-mono">{formatTokens(monthlyTokens.outputTokens)}</span>
                </div>
              </div>
            </div>

            {/* 5-Year Comparison */}
            <div className="grid grid-cols-2 gap-4">
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Server className="h-4 w-4 text-destructive" />
                  <span className="font-medium">On-Premises (5 Years)</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">CapEx (Upfront)</span>
                    <span>{formatCurrency(onPremBreakdown.capexTotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">OpEx (60 months)</span>
                    <span>{formatCurrency(onPremBreakdown.monthlyOpEx * 60)}</span>
                  </div>
                  <div className="flex justify-between font-bold border-t pt-2 mt-2">
                    <span>Total TCO</span>
                    <span>{formatCurrency(comparison.fiveYearOnPremTco)}</span>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Cloud className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Cloud AI (5 Years)</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Model</span>
                    <span>{config.aiServiceModel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Growth Rate</span>
                    <span>{config.annualGrowthRate}% / year</span>
                  </div>
                  <div className="flex justify-between font-bold border-t pt-2 mt-2">
                    <span>Total Spend</span>
                    <span>{formatCurrency(comparison.fiveYearCloudSpend)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Verdict */}
            {comparison.savings !== 0 && (
              <div className={`rounded-lg p-4 ${comparison.savings > 0 ? 'bg-success/10 border border-success/30' : 'bg-destructive/10 border border-destructive/30'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className={`font-medium ${comparison.savings > 0 ? 'text-success' : 'text-destructive'}`}>
                      {comparison.savings > 0 ? 'On-Prem is More Cost Effective' : 'Cloud is More Cost Effective'}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {comparison.savings > 0 
                        ? `Save ${formatCurrency(comparison.savings)} (${comparison.savingsPercent.toFixed(0)}%) over 5 years`
                        : `Cloud saves ${formatCurrency(Math.abs(comparison.savings))} over 5 years`
                      }
                    </p>
                  </div>
                  {comparison.breakEvenMonth && (
                    <Badge variant="outline">
                      Break-even: {comparison.breakEvenMonth}
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      {/* Cloud Cost Compare Tab */}
      <TabsContent value="cloud" className="mt-4">
        <CloudCostComparePanel
          config={config}
          monthlyTokens={monthlyTokens}
          monthlyRollups={monthlyRollups}
          comparison={comparison}
        />
      </TabsContent>

      {/* On-Prem TCO Tab */}
      <TabsContent value="onprem" className="mt-4">
        <OnPremTcoPanel
          config={config}
          onPremBreakdown={onPremBreakdown}
          serverCount={serverCount}
          gpuCount={snapshot?.finalGpuCount ?? 0}
          monthlyRollups={monthlyRollups}
        />
      </TabsContent>

      {/* Methodology Tab */}
      <TabsContent value="methodology" className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Calculation Methodology
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 text-sm">
            <div>
              <h4 className="font-medium mb-2">Token Volume Estimation</h4>
              <div className="bg-muted/50 rounded-lg p-3 font-mono text-xs space-y-1">
                <p>working_seconds_per_month = work_hours × 3600 × work_days</p>
                <p>monthly_input_tokens = avg_prompt_tps × working_seconds × growth_factor</p>
                <p>monthly_output_tokens = avg_decode_tps × working_seconds × growth_factor</p>
                <p className="text-muted-foreground">// Growth factor = (1 + annual_rate)^(month/12)</p>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2">Cloud Cost Calculation</h4>
              <div className="bg-muted/50 rounded-lg p-3 font-mono text-xs space-y-1">
                <p>cloud_input_cost = (input_tokens / 1,000,000) × input_price</p>
                <p>cloud_output_cost = (output_tokens / 1,000,000) × output_price</p>
                <p>monthly_cloud_cost = cloud_input_cost + cloud_output_cost</p>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2">On-Prem TCO Calculation</h4>
              <div className="bg-muted/50 rounded-lg p-3 font-mono text-xs space-y-1">
                <p>capex_total = servers × capex_per_server</p>
                <p>monthly_power = kw_per_server × 24 × 30 × $/kwh × servers</p>
                <p>monthly_admin = admin_per_server × servers</p>
                <p>monthly_opex = power + admin + facilities + personnel</p>
                <p>month_0_cost = capex_total + monthly_opex</p>
                <p>subsequent_months = monthly_opex</p>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2">Break-Even Calculation</h4>
              <div className="bg-muted/50 rounded-lg p-3 font-mono text-xs">
                <p>break_even = first month where cumulative_onprem ≤ cumulative_cloud</p>
              </div>
            </div>

            <div className="text-muted-foreground">
              <h4 className="font-medium mb-2 text-foreground">Assumptions</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Token throughput derived from Monte Carlo simulation in Sizing Engine</li>
                <li>Servers run 24/7 for power calculation (even if workload is only during work hours)</li>
                <li>CapEx is paid upfront in month 0 (not amortized for cumulative TCO)</li>
                <li>No additional hardware needed for growth (infrastructure is pre-provisioned)</li>
                <li>Cloud pricing uses current rates without volume discounts</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};
